/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: TimeConditionsPage.tsx
 ??? Created: 2025-01-15
 ?? Description:
    Time Conditions list page with table view, search, pagination, and actions.
    Allows viewing, creating, editing, duplicating, and deleting time conditions.

 ?? Notes:
    - Domain-scoped (multi-tenant safe)
    - Supports search and pagination
    - Shows time group and destination info for each condition

 ?? Last Updated: 2025-11-07 by GPT-5 Codex
----------------------------------------------------------------*/

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Copy, Edit, Trash2 } from 'lucide-react';
import { ModulePageContainer } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import DataGridSimple from '../../components/ui/DataGridSimple';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useNavigationStore } from '../../store/navigation';
import { deleteTimeConditionById, navigateToTimeConditionDuplicate } from './timeConditionsActions';
import { useTimeConditionList } from './hooks/useTimeConditionList';
import { TimeCondition } from './types';
import { useDestinationOptions, resolveDestinationLabel } from '../../hooks/useDestinationOptions';

const ACTION_BUTTON_CLASSNAMES = {
  base: 'p-2 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60',
  edit: 'hover:bg-blue-100 dark:hover:bg-blue-900/30',
  duplicate: 'hover:bg-emerald-100 dark:hover:bg-emerald-900/30',
  delete: 'hover:bg-red-100 dark:hover:bg-red-900/30',
};


export default function TimeConditionsPage() {
  const { t } = useI18n();
  const { setCurrentPageTitle } = useNavigationStore();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();

  const pageTitle = useMemo(() => t('timeConditions.title'), [t]);

  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [conditionToDelete, setConditionToDelete] = useState<TimeCondition | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    data,
    loading,
    columns: baseColumns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useTimeConditionList();

  // Collect unique destination modules from the list data
  const uniqueModules = useMemo(() => {
    const modules = new Set<string>();
    data.forEach((item) => {
      if (item.destinationMatchModule) modules.add(item.destinationMatchModule);
      if (item.destinationNomatchModule) modules.add(item.destinationNomatchModule);
    });
    return Array.from(modules).filter((m) => m !== 'terminate_call');
  }, [data]);

  // Preload each unique module into the destination cache
  // Each hook call loads one module; we support up to 6 unique modules
  useDestinationOptions(uniqueModules[0] ?? '');
  useDestinationOptions(uniqueModules[1] ?? '');
  useDestinationOptions(uniqueModules[2] ?? '');
  useDestinationOptions(uniqueModules[3] ?? '');
  useDestinationOptions(uniqueModules[4] ?? '');
  useDestinationOptions(uniqueModules[5] ?? '');

  // Add custom renders to columns
  const columns = useMemo(
    () =>
      baseColumns.map((column) => {
        if (column.key === 'name') {
          return {
            ...column,
            render: (_value: unknown, item: TimeCondition) => (
              <span className="font-medium text-foreground">{item.name}</span>
            ),
          };
        }
        if (column.key === 'description') {
          return {
            ...column,
            render: (_value: unknown, item: TimeCondition) => (
              <span className="block max-w-md truncate text-muted-foreground">
                {item.description || t('common:notAvailable')}
              </span>
            ),
          };
        }
        if (column.key === 'timeGroup') {
          return {
            ...column,
            render: (_value: unknown, item: TimeCondition) => (
              <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                {item.timeGroup?.name || item.timeGroupName || t('common:notAvailable')}
              </span>
            ),
          };
        }
        if (column.key === 'destinations') {
          return {
            ...column,
            render: (_value: unknown, item: TimeCondition) => {
              // Helper function to translate destination
              const translateDestination = (module: string | undefined, value: string | undefined) => {
                if (!module) return null;
                
                // Translate module name
                const moduleLabel = t(`outboundRoutes.modules.${module}.label`, { defaultValue: module });
                
                // For terminate_call, translate the cause code
                if (module === 'terminate_call' && value) {
                  const causeLabel = t(
                    `outboundRoutes.terminateCauses.${value}.label`,
                    { defaultValue: value }
                  );
                  return `${moduleLabel} � ${causeLabel}`;
                }
                
                // For other modules, resolve name from destination options cache
                if (value) {
                  const resolvedLabel = resolveDestinationLabel(module, value);
                  return `${moduleLabel} � ${resolvedLabel ?? value}`;
                }

                return moduleLabel;
              };

              const matchDestination = translateDestination(item.destinationMatchModule, item.destinationMatchValue);
              const nomatchDestination = translateDestination(item.destinationNomatchModule, item.destinationNomatchValue);

              return (
                <div className="text-xs">
                  {matchDestination ? (
                    <div className="text-green-600 dark:text-green-400">
                      ? {matchDestination}
                    </div>
                  ) : null}
                  {nomatchDestination ? (
                    <div className="text-red-600 dark:text-red-400">
                      ? {nomatchDestination}
                    </div>
                  ) : null}
                  {!matchDestination && !nomatchDestination && (
                    <div className="text-muted-foreground">{t('common:notAvailable')}</div>
                  )}
                </div>
              );
            },
          };
        }
        if (column.key === 'status') {
          return {
            ...column,
            render: (_value: unknown, item: TimeCondition) => (
              <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                {statusLabels[item.status as keyof typeof statusLabels] || statusLabels.default}
              </span>
            ),
          };
        }
        if (column.key === 'createdAt') {
          return {
            ...column,
            render: (_value: unknown, item: TimeCondition) => {
              const date = new Date(item.createdAt);
              const isValid = !Number.isNaN(date.getTime());
              return (
                <span className="text-sm text-muted-foreground">
                  {isValid ? date.toLocaleDateString() : t('common:notAvailable')}
                </span>
              );
            },
          };
        }
        return column;
      }),
    [baseColumns, t],
  );

  const handleCreate = () => {
    navigate('/pbx/incoming-tools/time-conditions/new');
  };

  const handleEdit = (condition: TimeCondition) => {
    navigate(`/pbx/incoming-tools/time-conditions/${condition.uuid ?? condition.id}`);
  };

  const handleDuplicate = (condition: TimeCondition) => {
    navigateToTimeConditionDuplicate(navigate, condition.uuid ?? condition.id);
  };

  const handleDelete = useCallback((condition: TimeCondition) => {
    setConditionToDelete(condition);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!conditionToDelete) return;

    setIsDeleting(true);
    try {
      await deleteTimeConditionById(conditionToDelete.id);
      showSuccess(t('timeConditions.messages.deleteSuccess'));
      setShowDeleteModal(false);
      setConditionToDelete(null);
      refresh();
    } catch (error) {
      showError(error.message || t('timeConditions.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [conditionToDelete, refresh, showError, showSuccess, t]);

  const statusLabels = useMemo(
    () => ({
      default: t('timeConditions.fields.status.options.default'),
      'forced-match': t('timeConditions.fields.status.options.forcedMatch'),
      'forced-nomatch': t('timeConditions.fields.status.options.forcedNomatch'),
    }),
    [t],
  );

  const renderActions = useCallback(
    (condition: TimeCondition) => (
      <div className="flex items-center gap-2">
        <SimpleTooltip content={t('common:edit')} position="top">
          <button
            type="button"
            onClick={() => handleEdit(condition)}
            aria-label={t('common:edit')}
            className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.edit}`}
          >
            <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('common:duplicate')} position="top">
          <button
            type="button"
            onClick={() => handleDuplicate(condition)}
            aria-label={t('common:duplicate')}
            className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.duplicate}`}
          >
            <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('common:delete')} position="top">
          <button
            type="button"
            onClick={() => handleDelete(condition)}
            aria-label={t('common:delete')}
            className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.delete}`}
          >
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </button>
        </SimpleTooltip>
      </div>
    ),
    [handleDelete, handleDuplicate, handleEdit, t],
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <>
          <DataGridSimple
            data={data}
            columns={columns.map((column) => ({
              ...column,
              render:
                column.key === 'actions'
                  ? (_value, item: TimeCondition) => renderActions(item)
                  : column.render,
            }))}
            loading={loading}
            pagination={pagination}
            searchQuery={searchQuery}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSearch={handleSearch}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            onColumnToggle={handleColumnToggle}
            onAdd={handleCreate}
            title={t('timeConditions.title')}
            titleIcon={<Clock className="h-5 w-5 text-primary" />}
            addButtonText={t('timeConditions.actions.create')}
            searchPlaceholder={t('timeConditions.searchPlaceholder')}
            emptyMessage={t('timeConditions.noResults')}
          />

          <ConfirmationModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={confirmDelete}
            title={t('timeConditions.messages.confirmDeleteTitle')}
            message={t('timeConditions.messages.confirmDeleteMessage', {
              name: conditionToDelete?.name ?? '',
            })}
            confirmText={t('common:delete')}
            cancelText={t('common:cancel')}
            type="danger"
            isLoading={isDeleting}
          />
        </>
      }
      formView={null}
    />
  );
}

